// src/pages/outsourcing_management/InquiryPartnerShortlist.tsx
//
// Who this inquiry is being asked of, before one of them is settled on.
// `partner` on the inquiry stays the decision; this is the shortlist that
// precedes it. Asking three suppliers for a price used to mean three
// inquiries, or overwriting the supplier field each time - and neither
// recorded that the other two were ever asked.
//
// Deliberately not a multi-select on the existing SupplierAutocomplete: a
// candidate is a row with its own send state, not an item in a value array,
// and every one that has been written to can no longer be removed.
//
// Presentational: it reports edits and holds no state. The shortlist is
// edited inside InquiryEditForm, which keeps the pending set in form state
// and writes it on Save with everything else - these rows used to call
// their own endpoints as they were clicked, which meant Cancel did not
// cancel them.
import { FunctionComponent } from "react";
import {
  Box,
  Chip,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { SupplierAutocomplete } from "components/Dashboard/supplierOption";
import { InquiryPartnerCandidateType } from "types/InquiryTypes";
import { reformatDateTime } from "util/DateUtil";

type PropsType = {
  candidates: InquiryPartnerCandidateType[];
  /** False outside the edit form, and once the inquiry is finished. */
  editable: boolean;
  /**
   * The inquiry's type, so the picker offers the suppliers registered for
   * that service rather than all of them. A soft filter - see
   * SupplierAutocomplete.
   */
  serviceType?: string;
  /** Every one omitted when `editable` is false. A picked supplier is added
   *  the moment it is picked, to be emailed; the row's switch turns that off. */
  onAdd?: (partnerId: number, sendEmail: boolean) => void;
  onRemove?: (candidateId: number) => void;
  onSetSendEmail?: (candidateId: number, sendEmail: boolean) => void;
};

const InquiryPartnerShortlist: FunctionComponent<PropsType> = ({
  candidates,
  editable,
  serviceType,
  onAdd,
  onRemove,
  onSetSendEmail,
}) => {
  // Picking is adding - there used to be a + button after the picker, and
  // it was one click too many. The picker stays empty (its value is never
  // set), so it is ready for the next one. Picking somebody already listed
  // does nothing.
  const add = (partnerId: number | undefined) => {
    if (!partnerId || candidates.some((c) => c.partner_id === partnerId)) return;
    onAdd?.(partnerId, true);
  };

  return (
    <Box>
      {candidates.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Nobody on the shortlist yet — the inquiry goes to its supplier alone.
        </Typography>
      )}

      {candidates.map((candidate) => (
        <Box
          key={candidate.id}
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
        >
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {candidate.partner_name ?? `Partner #${candidate.partner_id}`}
          </Typography>
          {candidate.is_final && (
            <Chip size="small" color="success" label="Chosen" />
          )}
          {candidate.sent_at ? (
            <Tooltip title={`Sent ${reformatDateTime(candidate.sent_at)}`}>
              <Chip size="small" variant="outlined" label="Sent" />
            </Tooltip>
          ) : candidate.send_email ? (
            // "Not sent" described neither the action nor the state - it read
            // as a failure when it is an intention. This chip means the
            // broadcast will include them next time it runs.
            <Tooltip title="Send to Supplier will include this one">
              <Chip size="small" variant="outlined" label="To send email" />
            </Tooltip>
          ) : (
            // Not the same as "not sent yet" - this one is never going to be.
            <Tooltip title="Approached another way - the send skips this one">
              <Chip size="small" variant="outlined" label="No email" />
            </Tooltip>
          )}
          {editable && (
            <Tooltip
              title={
                candidate.sent_at
                  ? "Already sent - this can no longer be changed"
                  : candidate.send_email
                  ? "Include this supplier when the inquiry is sent"
                  : "Skip this supplier when the inquiry is sent"
              }
            >
              <span>
                <Switch
                  size="small"
                  checked={candidate.send_email}
                  disabled={!!candidate.sent_at}
                  onChange={(e) =>
                    onSetSendEmail?.(candidate.id, e.target.checked)
                  }
                />
              </span>
            </Tooltip>
          )}
          {editable && (
            // A supplier who already has the RFQ cannot be removed: their
            // reply would arrive against a candidate that no longer exists,
            // and it would erase that they were asked at all. The backend
            // refuses it too - this is so the button does not lie.
            <Tooltip
              title={
                candidate.sent_at
                  ? "Already sent this inquiry — can no longer be removed"
                  : "Remove from the shortlist"
              }
            >
              <span>
                <IconButton
                  size="small"
                  disabled={!!candidate.sent_at}
                  onClick={() => onRemove?.(candidate.id)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      ))}

      {editable && (
        <Box sx={{ mt: 1 }}>
          {/* Remounted on every add: the value never changes, so the input
              would otherwise keep showing the name it just added. */}
          <SupplierAutocomplete
            key={candidates.length}
            value={undefined}
            onChange={add}
            label="Add a supplier"
            serviceType={serviceType}
          />
        </Box>
      )}
    </Box>
  );
};

export default InquiryPartnerShortlist;
